import { SimpleLinearRegression } from 'ml-regression';
import SnowflakeService from './snowflakeService.js';

class LiveGMVService {
  constructor() {
    this.currentGMV = 0;
    this.displayedGMV = 0;
    this.extrapolatedGMV = 0;
    this.lastFetchTime = null;
    this.intervalId = null;
    this.animationId = null;
    this.updateInterval = 24 * 60 * 60 * 1000; // Default 24 hours in milliseconds
    this.isRunning = false;
    this.callbacks = [];
    this.gmvData = [];

    // Animation settings for more frequent "live" feel
    this.minUpdateDelay = 500; // Minimum 200ms between updates
    this.maxUpdateDelay = 2000; // Maximum 1.5 seconds between updates
    this.targetReachTime = null;

    // Natural variation settings
    this.burstMode = false; // Whether we're in a burst of quick updates
    this.lastUpdateTime = 0;
    this.momentum = 0; // Current momentum for increment sizes
  }

  // Subscribe to GMV updates
  subscribe(callback) {
    this.callbacks.push(callback);
    return () => {
      this.callbacks = this.callbacks.filter(cb => cb !== callback);
    };
  }

  // Notify all subscribers
  notifySubscribers() {
    this.callbacks.forEach(callback => {
      callback({
        displayedGMV: this.displayedGMV,
        currentGMV: this.currentGMV,
        extrapolatedGMV: this.extrapolatedGMV,
        lastFetchTime: this.lastFetchTime,
        isExtrapolating: this.displayedGMV < this.extrapolatedGMV,
        timeToTarget: this.targetReachTime ? Math.max(0, this.targetReachTime - Date.now()) : 0
      });
    });
  }

  // Set the update interval (in milliseconds)
  setUpdateInterval(milliseconds) {
    this.updateInterval = milliseconds;
    if (this.isRunning) {
      this.stop();
      this.start();
    }
  }

  // Fetch fresh data and calculate extrapolation
  async fetchAndExtrapolate() {
    try {

      const result = await SnowflakeService.getGMVData();

      if (!result.success || !result.data || result.data.length === 0) {
        console.error('❌ Failed to fetch GMV data:', result.error);
        return false;
      }

      this.gmvData = result.data;
      this.currentGMV = process.env.NODE_ENV == 'development' ? this.gmvData[this.gmvData.length - 1].total_gmv_lifetime : this.gmvData[this.gmvData.length - 1].total_gmv_lifetime;
      this.lastFetchTime = new Date();

      // Calculate extrapolated GMV using regression
      this.extrapolatedGMV = this.calculateExtrapolatedGMV();

      // Set the target reach time (end of current interval)
      this.targetReachTime = Date.now() + this.updateInterval;

      // Start displaying from current GMV
      this.displayedGMV = this.currentGMV;

      /*
      console.log(`✅ Data fetched successfully:`);
      console.log('First data point GMV:', this.gmvData[0].TOTAL_GMV_LIFETIME);
      console.log('Last data point GMV:', this.currentGMV);
      console.log(`   Current GMV: $${this.currentGMV.toLocaleString()}`);
      console.log(`   Displayed GMV: $${this.displayedGMV.toLocaleString()}`);
      console.log(`   Extrapolated GMV: $${this.extrapolatedGMV.toLocaleString()}`);
      console.log(`   Growth prediction: $${(this.extrapolatedGMV - this.currentGMV).toLocaleString()}`);
      */

      // Immediately notify subscribers with initial data
      this.notifySubscribers();
      return true;
    } catch (error) {
      console.error('❌ Error in fetchAndExtrapolate:', error);
      return false;
    }
  }

  // Calculate extrapolated GMV using linear regression
  calculateExtrapolatedGMV() {
    if (this.gmvData.length < 2) {
      // Not enough data for regression, return current GMV
      return this.currentGMV;
    }

    try {
      // Use the last 30 days of data (or all available data if less than 30 days)
      const recentData = this.gmvData.slice(-30);

      // Prepare data for regression (x = day index, y = GMV)
      const x = recentData.map((_, index) => index);
      const y = process.env.NODE_ENV == 'development' ? recentData.map(item => item.total_gmv_lifetime) : recentData.map(item => item.total_gmv_lifetime);


      // Create and train the regression model
      const regression = new SimpleLinearRegression(x, y);

      // Calculate how many intervals ahead to predict
      const intervalHours = this.updateInterval / (1000 * 60 * 60);
      const intervalsPerDay = 24 / intervalHours;
      const daysToPredict = 1 / intervalsPerDay; // Fraction of a day

      // Predict GMV at the end of the current interval
      const nextX = recentData.length - 1 + daysToPredict;
      const predictedGMV = regression.predict(nextX);

      // Ensure prediction is not negative and not unreasonably high
      const maxReasonableGrowth = this.currentGMV * 1.1; // Max 10% growth per interval
      const minGMV = this.currentGMV; // Can't go below current

      return Math.max(minGMV, Math.min(maxReasonableGrowth, predictedGMV));
    } catch (error) {
      console.error('❌ Error in regression calculation:', error);
      // Fallback: use simple growth based on recent trend
      return this.calculateFallbackExtrapolation();
    }
  }

  // Fallback extrapolation method
  calculateFallbackExtrapolation() {
    if (this.gmvData.length < 2) {
      return this.currentGMV;
    }

    // Calculate average daily growth from last 7 days
    const last7Days = this.gmvData.slice(-7);
    let totalGrowth = 0;

    for (let i = 1; i < last7Days.length; i++) {
      const dailyGrowth = last7Days[i].TOTAL_GMV_LIFETIME - last7Days[i - 1].TOTAL_GMV_LIFETIME;
      totalGrowth += dailyGrowth;
    }

    const avgDailyGrowth = totalGrowth / (last7Days.length - 1);

    // Calculate fraction of day for this interval
    const intervalHours = this.updateInterval / (1000 * 60 * 60);
    const fractionOfDay = intervalHours / 24;

    // Extrapolate based on average growth
    const extrapolatedGrowth = avgDailyGrowth * fractionOfDay;

    return this.currentGMV + Math.max(0, extrapolatedGrowth);
  }

  // Start the gradual animation to target with natural variations
  startGradualAnimation() {
    if (this.animationId) {
      clearTimeout(this.animationId);
    }

   // console.log(`🎬 Starting animation: displayed=${this.displayedGMV}, target=${this.extrapolatedGMV}`);

    // Check if we've reached the target (with small tolerance for floating point)
    if (Math.abs(this.displayedGMV - this.extrapolatedGMV) < 0.01) {
    //  console.log('🎯 Already at target, no animation needed');
      this.displayedGMV = this.extrapolatedGMV;
      this.notifySubscribers();
      return;
    }

    const timeRemaining = Math.max(1000, this.targetReachTime - Date.now());
    const gmvRemaining = this.extrapolatedGMV - this.displayedGMV;


    if (timeRemaining <= 0) {
    //  console.log('⏰ Time expired, jumping to target');
      this.displayedGMV = this.extrapolatedGMV;
      this.notifySubscribers();
      return;
    }

    // Natural timing patterns
    let delay;
    let incrementMultiplier = 1;

    // Randomly enter/exit burst mode (20% chance to start, 30% chance to stop)
    if (!this.burstMode && Math.random() < 0.1) {
      this.burstMode = true;
      this.momentum = 1.5; // Start with higher momentum
    } else if (this.burstMode && Math.random() < 0.3) {
      this.burstMode = false;
      this.momentum = 0.5; // Slow down after burst
    }

    if (this.burstMode) {
      // Quick burst updates (200-600ms)
      delay = Math.random() * 600 + 400;
      incrementMultiplier = 0.8 + Math.random() * 0.4; // Smaller but more frequent
    } else {
      // Normal updates with occasional pauses
      if (Math.random() < 0.15) {
        // Occasional longer pause (simulates real-world data gaps)
        delay = Math.random() * 2000 + 1000;
        incrementMultiplier = 1.5 + Math.random() * 1; // Bigger increment after pause
      } else {
        // Regular timing
        delay = Math.random() * (this.maxUpdateDelay - this.minUpdateDelay) + this.minUpdateDelay;
        incrementMultiplier = 1 + Math.random() * .5;
      }
    }

    // Apply momentum decay
    this.momentum = Math.max(0.3, this.momentum * 0.95);
    incrementMultiplier *= this.momentum;

    // Calculate increment with natural variation
    const baseTimeRatio = delay / timeRemaining;
    const naturalVariation = 0.3 + Math.random() * 1.4; // More varied than before
    const increment = gmvRemaining * baseTimeRatio * incrementMultiplier * naturalVariation;
    // Apply the increment
    this.displayedGMV = Math.min(this.extrapolatedGMV, this.displayedGMV + increment);

    this.lastUpdateTime = Date.now();
    this.notifySubscribers();

    // Schedule next update
    this.animationId = setTimeout(() => {
      this.startGradualAnimation();
    }, delay);
  }

  // Start the live tracking
  async start() {
    if (this.isRunning) {
      console.log('⚠️ Live GMV tracker is already running');
      return;
    }

   // console.log(`🚀 Starting live GMV tracker with ${this.updateInterval / 1000}s intervals`);

    this.isRunning = true;

    // Initial data fetch and extrapolation
    const success = await this.fetchAndExtrapolate();

    if (success) {
      // Start gradual animation
      this.startGradualAnimation();

      // Schedule periodic data refetch
      this.intervalId = setInterval(async () => {
     //   console.log('⏰ Interval reached - refetching data and recalculating extrapolation');

        // Stop current animation
        if (this.animationId) {
          clearTimeout(this.animationId);
        }

        // Fetch fresh data and extrapolate
        const success = await this.fetchAndExtrapolate();

        if (success) {
          // Start new animation cycle
          this.startGradualAnimation();
        }
      }, this.updateInterval);
    } else {
      this.isRunning = false;
      throw new Error('Failed to initialize live GMV tracker');
    }
  }

  // Stop the live tracking
  stop() {
    console.log('🛑 Stopping live GMV tracker');

    this.isRunning = false;

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.animationId) {
      clearTimeout(this.animationId);
      this.animationId = null;
    }
  }

  // Get current status
  getStatus() {
    return {
      isRunning: this.isRunning,
      currentGMV: this.currentGMV,
      displayedGMV: this.displayedGMV,
      extrapolatedGMV: this.extrapolatedGMV,
      lastFetchTime: this.lastFetchTime,
      updateInterval: this.updateInterval,
      timeToNextFetch: this.intervalId ? this.updateInterval - ((Date.now() - this.lastFetchTime?.getTime()) || 0) : 0,
      timeToTarget: this.targetReachTime ? Math.max(0, this.targetReachTime - Date.now()) : 0,
      isExtrapolating: this.displayedGMV < this.extrapolatedGMV
    };
  }

  // Format currency for display
  formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
}

export default new LiveGMVService();
